// Hubspot.jsx

import { useEffect, useState } from 'react'
import { useApi } from '../../../../api/api'
import { useSelector } from 'react-redux'
import datasourceDefs from '../../../../api/datasourceConfig'
import {
    ScoopTextField,
    ScoopButton,
    ScoopDialog,
    ScoopLinearProgress,
    ScoopText,
    ScoopSelector,
    useToast,
} from '../../../../components/ui'
import styles from './Hubspot.module.scss'

const bot = datasourceDefs.find((b) => b.name === 'Hubspot')

export const Hubspot = ({
    open,
    onClose,
    stepBack,
    type,
    initialDatasetDetails,
    basicConfiguration,
}) => {
    const toast = useToast()
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const { postData } = useApi(bot.API_URL)
    const [url, setUrl] = useState('https://app.hubspot.com/reports-list/...')
    const [isUrlValid, setIsUrlValid] = useState(true)
    const [reports, setReports] = useState([])
    const [selectedReport, setSelectedReport] = useState('')
    const [isReportTransactional, setIsReportTransactional] = useState(false)
    const [allowMutipleLoads, setAllowMutipleLoads] = useState(false)
    const [reportName, setReportName] = useState('')
    const [reportDescription, setReportDescription] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [nextStep, setNextStep] = useState(false)
    const [partialReportName, setPartialReportName] = useState('') //used as partial search string to whittle down report names
    // set if user is editing vs creating new
    const [isEditMode, setIsEditMode] = useState(false)
    const [inboxName, setInboxName] = useState(null) //used only if inboxName is passed in as part of update call
    const [inboxID, setInboxID] = useState(null) //used only if inboxID is passed in as part of update call

    useEffect(() => {
        if (initialDatasetDetails) {
            setIsEditMode(true)
            setReportDescription(initialDatasetDetails.description)
            setReportName(initialDatasetDetails.label)
            setInboxName(initialDatasetDetails.inboxName)
            setIsReportTransactional(initialDatasetDetails.isTransactional || false)
            setAllowMutipleLoads(initialDatasetDetails.enableMultipleSameDayLoads || false)
            setInboxID(initialDatasetDetails.inboxID)
            setUrl(initialDatasetDetails.reportLink)
        }
    }, [initialDatasetDetails])

    const handleUrlChange = (e) => {
        const newUrl = e.target.value
        setUrl(newUrl)

        // Validate URL
        setIsUrlValid(bot.urlValidation.test(newUrl))
    }

    const handlePartialReportNameChange = (e) => {
        const newPartialReportName = e.target.value
        setPartialReportName(newPartialReportName)
    }

    const readReports = async () => {
        const action = {
            action: 'listReports',
            service: bot.service,
            reportURL: url,
            botUID: bot.botUID,
            label: partialReportName,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        return result
    }

    const handleGetReports = async () => {
        setIsLoading(true)
        try {
            const data = await readReports()
            setReports(JSON.parse(data.reports))
        } catch (error) {
            console.log(error)
        }
        setIsLoading(false)
    }

    useEffect(() => {
        loadedReports()
    }, [reports])

    const loadedReports = () => {
        if (reports.length > 0 && selectedReport === '') {
            setSelectedReport(reports[0])
        }
    }

    function createInboxName(userID, selectedReport) {
        // Combine userID and selectedReport with an underscore
        let combinedName = `${userID}_${selectedReport.substring(0, 30)}`

        // Replace characters not in the allowed list with an underscore
        // Allowed characters: alphanumeric, plus (+), minus (-), equals (=), dot (.), comma (,), underscore (_), colon (:), at (@)
        let scrubbedName = combinedName.replace(/[^a-zA-Z0-9\+\-\=\.\,\_\:\@]/g, '_')

        // Generate a random 3-digit number to help ensure uniqueness
        const randomDigits = Math.floor(100 + Math.random() * 900)

        // Append the random 3-digit number to the scrubbedName
        return `${scrubbedName}_${randomDigits}`
    }

    const createBot = async () => {
        const action = {
            action: 'createRobot',
            inboxName: createInboxName(userID, selectedReport),
            label: selectedReport,
            keepOnlyCurrent: basicConfiguration.isMostRecent,
            incremental: basicConfiguration.isIncremental,
            enableMultipleSameDayLoads: basicConfiguration.isMultipleLoads,
            description: reportDescription,
            workspaceID: workspaceID,
            reportURL: url,
            isTransactional: type === 'Transactional',
            userName: userID,
            service: bot.service,
            botUID: bot.botUID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        return result
    }

    const handleCreateBot = () => {
        createBot().then((results) => {
            // console.log(results)
            if (results?.status === 'success') toast.success('Hubspot bot created successfully')
            else if (results?.status === 'error')
                toast.error(results?.message ?? 'Failed to create Hubspot Bot')
            setNextStep(false)
            stepBack()
            onClose()
        })
    }

    // Convert reports array to options for ScoopSelector
    const reportOptions = reports.map((report) => ({
        label: report,
        value: report,
    }))

    return (
        <>
            {!nextStep ? (
                <ScoopDialog
                    open={open}
                    title={'Report from Hubspot'}
                    icon={bot.icon}
                    onClose={onClose}
                    maxWidth="lg"
                    actions={
                        <>
                            <ScoopButton variant="grey" size="sm" onClick={stepBack}>
                                Back
                            </ScoopButton>
                            <ScoopButton
                                size="sm"
                                onClick={() => setNextStep(true)}
                                disabled={selectedReport === ''}
                            >
                                {' '}
                                I've done this{' '}
                            </ScoopButton>
                        </>
                    }
                >
                    <ScoopText className={styles.headerText}>
                        Share your Google Analytics report with Scoop to start importing data
                    </ScoopText>
                    <div className={styles.dialogContent}>
                        <div className={styles.stepper}>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>1</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Share the report with Hubspot account:{' '}
                                        <a>
                                            <b>{bot.botUID}</b>
                                        </a>
                                    </ScoopText>
                                </div>
                            </div>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>2</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Paste the entire report URL below
                                    </ScoopText>
                                </div>
                                <div className={styles.stepContent}>
                                    <ScoopTextField
                                        label="Hubspot Reports URL"
                                        value={url}
                                        onChange={handleUrlChange}
                                    />
                                    {!isUrlValid && (
                                        <ScoopText className={styles.errorText}>
                                            Invalid Hubspot Reports URL
                                        </ScoopText>
                                    )}
                                    <ScoopTextField
                                        label="Partial name of report"
                                        value={partialReportName}
                                        onChange={handlePartialReportNameChange}
                                    />
                                    {url && (
                                        <ScoopButton
                                            size="sm"
                                            onClick={handleGetReports}
                                            className={styles.fetchButton}
                                        >
                                            Fetch Reports List
                                        </ScoopButton>
                                    )}
                                </div>
                            </div>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>3</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Select Report
                                    </ScoopText>
                                </div>
                                <div className={styles.stepContent}>
                                    {reports.length > 0 && (
                                        <ScoopSelector
                                            value={selectedReport || ''}
                                            onChange={(e) => {
                                                setSelectedReport(e.target.value)
                                            }}
                                            options={reportOptions}
                                            className={styles.selector}
                                        />
                                    )}
                                </div>
                            </div>

                            {isLoading && (
                                <ScoopLinearProgress
                                    variant="indeterminate"
                                    className={styles.fetchButton}
                                />
                            )}
                        </div>
                    </div>
                </ScoopDialog>
            ) : (
                <ScoopDialog
                    open={open}
                    title={'New Report from Hubspot'}
                    icon={bot.icon}
                    onClose={onClose}
                    actions={
                        <>
                            <ScoopButton
                                variant="grey"
                                size="sm"
                                onClick={() => setNextStep(false)}
                            >
                                Back
                            </ScoopButton>
                            <ScoopButton size="sm" onClick={() => handleCreateBot()}>
                                {' '}
                                Create Report{' '}
                            </ScoopButton>
                        </>
                    }
                    className={styles.dialogNarrow}
                >
                    <div className={styles.infoBox}>
                        <ScoopText>
                            {' '}
                            data will be Scooped from <b>{selectedReport}</b>
                        </ScoopText>
                    </div>
                    <ScoopTextField
                        label={'Dataset name'}
                        onChange={(event) => {
                            setReportName(event.target.value)
                            setReportDescription(event.target.value)
                        }}
                        value={reportName}
                    />
                </ScoopDialog>
            )}
        </>
    )
}
